// externals
import type { SyntheticEvent } from 'react';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { Button } from '@interstate/components/Button';

// components
import { VehicleListRowItemInStockCompactNotSelected } from './VehicleListRowItemInStockCompactNotSelected';
import { VehicleListRowItemInStockCompactSelectedWithoutInputs } from './VehicleListRowItemInStockCompactSelectedWithoutInputs';
import { VehicleListRowItemInStockCompactSelectedWithInputs } from './VehicleListRowItemInStockCompactSelectedWithInputs';

// utils
import { useVehicleListRowItemInStock } from '../useVehicleListRowItemInStock';
import { useVehicleSearchFiltersContext } from '../../../changeVehicle/VehicleSearchFiltersContext';

// styles
import {
    VehicleListRowItemContainer,
    VehicleListRowItemContentContainer,
    VehicleListRowItemVisuallyHiddenStatus
} from '../../VehicleListRowItem.interstate.style';

// types
import type { VehicleListItemProps } from '../VehicleListRowItemInStock.types';

export const VehicleListRowItemInStockCompact = ({ vehicle }: VehicleListItemProps) => {
    const theme = useInterstateTheme();

    const vehicleData = useVehicleListRowItemInStock(vehicle);
    const {
        vin,
        isSelected,
        shouldShowInputs,
        selectVehicle,
        isInlineActionsDisabled,
        handleSelectVehicleClick,
        canAddNewScenario,
        isAddNewScenarioDisabled,
        handleAddNewScenarioClick,
        isChangeProcessing
    } = vehicleData;
    const { forceSmallBreakpoint } = useVehicleSearchFiltersContext();

    const compactUIItemClassName = 'vehicle-list-item-compact-ui';
    const selectedItemClassName = 'vehicle-list-item-selected';
    const className = isSelected ? `${selectedItemClassName} ${compactUIItemClassName}` : compactUIItemClassName;

    // Each selected branch places and sizes these itself, since their layouts differ.
    const renderInlineActions = () => (
        <>
            <Button
                buttonStyle="primary"
                data-testid={`btn-select-vehicle-${vin}`}
                disabled={isInlineActionsDisabled}
                isLoading={isChangeProcessing}
                onClick={(event: SyntheticEvent) => {
                    event.stopPropagation();
                    handleSelectVehicleClick();
                }}
            >
                Select Vehicle
            </Button>
            {/* Only hosts that supply an add-scenario action can create one (Manager View alone), and only once a
                worked scenario exists to clone — hidden during a pending First Pencil intercept. */}
            {canAddNewScenario && (
                <Button
                    buttonStyle="secondary"
                    data-testid={`btn-new-scenario-${vin}`}
                    disabled={isChangeProcessing || isAddNewScenarioDisabled}
                    onClick={(event: SyntheticEvent) => {
                        event.stopPropagation();
                        handleAddNewScenarioClick();
                    }}
                >
                    New Scenario
                </Button>
            )}
        </>
    );

    const renderContent = () => {
        if (!isSelected) {
            return (
                <VehicleListRowItemInStockCompactNotSelected
                    vehicleData={vehicleData}
                    forceSmallBreakpoint={forceSmallBreakpoint}
                />
            );
        }

        // Handed to the branch components rather than rendered here, so they sit inside the same grid column as the
        // vehicle details and inputs and stay aligned with them as the row narrows.
        const inlineActions = renderInlineActions();

        if (shouldShowInputs) {
            return (
                <VehicleListRowItemInStockCompactSelectedWithInputs
                    vehicleData={vehicleData}
                    forceSmallBreakpoint={forceSmallBreakpoint}
                    inlineActions={inlineActions}
                />
            );
        }

        return (
            <VehicleListRowItemInStockCompactSelectedWithoutInputs
                vehicleData={vehicleData}
                forceSmallBreakpoint={forceSmallBreakpoint}
                inlineActions={inlineActions}
            />
        );
    };

    return (
        <>
            <VehicleListRowItemContainer
                // Dropped on the selected row: `primaryAction` wraps `content` in a native <button>, and nesting the
                // action's <button> inside that is invalid HTML. Nothing is lost — `selectVehicle` already returns
                // early on a selected row — and every other row keeps its whole-row click.
                primaryAction={isSelected ? undefined : selectVehicle}
                data-testid={`vehicle-list-item-${vin}`}
                className={className}
                padding="8px 16px"
                selected={isSelected}
                theme={theme}
                content={<VehicleListRowItemContentContainer>{renderContent()}</VehicleListRowItemContentContainer>}
            />
            {/* Announces the actions rather than moving focus to them. The row was just clicked to select it, so
                taking focus again mid-task is more disruptive than a passive announcement.
                Mounted always, with empty text until the actions appear: a live region only announces a text
                *mutation* inside a region already in the accessibility tree, never its own insertion with text
                already present. One empty region per row announces nothing, so the cost is a bare span. */}
            <VehicleListRowItemVisuallyHiddenStatus role="status">
                {isSelected ? 'Vehicle actions available' : ''}
            </VehicleListRowItemVisuallyHiddenStatus>
        </>
    );
};
